import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  Compass,
  Flag,
  Library,
  Map,
  ScrollText,
  Search,
  Sparkles,
} from 'lucide-react';

const gatewayCards = [
  {
    title: 'The Books',
    description:
      'Enter the Sons of the Standard trilogy and follow those caught between Rome, tribe, loyalty, and survival.',
    path: '/books',
    icon: BookOpen,
  },
  {
    title: 'Featured Investigations',
    description:
      'Explore detailed enquiries combining landscape, archaeology, historical sources, and interpretation.',
    path: '/investigations',
    icon: Search,
  },
  {
    title: 'Interactive Maps',
    description:
      'Follow roads, forts, tribal territories, campaigns, and the changing geography of Roman Britain.',
    path: '/maps',
    icon: Map,
  },
  {
    title: 'Research Library',
    description:
      'Search original papers, browse military dossiers, and ask questions of the source-grounded archive assistant.',
    path: '/library',
    icon: Library,
  },
];

const researchRoutes = [
  {
    title: 'Research Paper Archive',
    description:
      'Original Roman Storyworld papers, historical syntheses, and archaeological studies.',
    path: '/research',
    icon: ScrollText,
  },
  {
    title: 'Roman Military Archive',
    description:
      'Legions, campaigns, fortresses, frontier systems, supply routes, and imperial crises.',
    path: '/military',
    icon: Flag,
  },
  {
    title: 'Conquest of Britannia',
    description:
      'A guided route through reliable overviews, campaign maps, battle studies, and specialist scholarship.',
    path: '/research/conquest',
    icon: Compass,
  },
];

const Home = () => {
  return (
    <main className="min-h-[100svh] bg-[#090806] text-[#f8f1e0]">
      <section className="relative min-h-[100svh] w-full overflow-hidden bg-black">
        {/* The current hero wording is part of these image files. */}
        <img
          src="/images/home/roman-storyworld-hero.jpg"
          alt="A Roman road leading through a dramatic ancient landscape at sunset"
          className="absolute inset-0 hidden h-full w-full object-cover object-top sm:block"
        />

        <img
          src="/images/home/roman-storyworld-hero-mobile.jpg"
          alt="A Roman road leading through a dramatic ancient landscape at sunset"
          className="absolute inset-0 h-full w-full object-cover object-center sm:hidden"
        />

        <div className="absolute inset-0 bg-black/20" />

        <div className="absolute left-0 right-0 top-[62svh] z-10 flex justify-center px-6 sm:top-[72vh] sm:px-0">
          <a
            href="#explore"
            className="
              inline-flex w-[55%] items-center justify-center gap-2
              rounded-md border border-amber-400/80
              bg-black/20 px-4 py-2
              text-xs font-medium text-amber-200
              shadow-lg backdrop-blur-[2px]
              transition hover:bg-amber-400/10 hover:text-amber-100
              focus:outline-none focus:ring-2 focus:ring-amber-300
              focus:ring-offset-2 focus:ring-offset-black
              sm:w-auto sm:px-5 sm:py-2.5 sm:text-sm
            "
          >
            <ScrollText className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            Begin the Journey
          </a>
        </div>
      </section>

      <section
        id="explore"
        className="border-t border-amber-500/20 bg-[#0d0b09] px-5 py-14 sm:px-8 sm:py-16"
      >
        <div className="mx-auto max-w-6xl">
          <div className="mx-auto max-w-3xl text-center">
            <Sparkles
              className="mx-auto mb-4 h-7 w-7 text-amber-400"
              aria-hidden="true"
            />
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.25em] text-amber-400">
              Story · Landscape · Evidence
            </p>
            <h1 className="text-2xl font-bold leading-tight text-amber-100 sm:text-3xl lg:text-4xl">
              Explore Roman Britain through story and surviving traces
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-base leading-7 text-stone-300 sm:text-lg">
              Roman Storyworld brings together historical fiction, original
              research, interactive maps, timelines, and a source-grounded
              research archive—helping readers move between story and evidence
              with confidence.
            </p>
          </div>

          <div className="mt-11 grid gap-5 sm:grid-cols-2">
            {gatewayCards.map((card) => {
              const Icon = card.icon;

              return (
                <Link
                  key={card.path}
                  to={card.path}
                  className="
                    group rounded-xl border border-white/10
                    bg-[#191512] p-6 shadow-xl
                    transition duration-200
                    hover:-translate-y-0.5 hover:border-amber-500/50
                    hover:bg-[#211a14]
                    focus:outline-none focus:ring-2 focus:ring-amber-300
                    focus:ring-offset-2 focus:ring-offset-[#0d0b09]
                  "
                >
                  <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-lg border border-amber-500/30 bg-amber-500/10">
                    <Icon
                      className="h-5 w-5 text-amber-300"
                      aria-hidden="true"
                    />
                  </div>

                  <h2 className="text-xl font-semibold text-amber-200">
                    {card.title}
                  </h2>
                  <p className="mt-3 leading-7 text-stone-300">
                    {card.description}
                  </p>

                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-amber-300">
                    Explore
                    <ArrowRight
                      className="h-4 w-4 transition-transform group-hover:translate-x-1"
                      aria-hidden="true"
                    />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-y border-amber-500/20 bg-[#120e0a] px-5 py-14 sm:px-8 sm:py-16">
        <div className="mx-auto max-w-6xl">
          <div className="overflow-hidden rounded-2xl border border-amber-500/35 bg-gradient-to-br from-[#24150b] via-[#1a120d] to-[#0d0b09] shadow-2xl">
            <div className="grid gap-8 p-7 sm:p-9 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:p-11">
              <div>
                <p className="mb-3 text-xs font-semibold uppercase tracking-[0.24em] text-amber-400">
                  A place examined closely
                </p>
                <h2 className="text-2xl font-bold text-amber-100 sm:text-3xl">
                  Featured Investigations
                </h2>
                <p className="mt-4 max-w-2xl leading-7 text-stone-300">
                  Follow long-form investigations in which roads, coastlines,
                  forts, inscriptions, sacred places, and surviving landscape
                  are considered beside the historical evidence.
                </p>
              </div>

              <div className="lg:text-right">
                <Link
                  to="/investigations"
                  className="
                    inline-flex items-center justify-center gap-2
                    rounded-lg bg-[#ff4b00] px-5 py-3
                    font-semibold text-white
                    transition hover:bg-[#ff6428]
                    focus:outline-none focus:ring-2 focus:ring-amber-300
                    focus:ring-offset-2 focus:ring-offset-[#120e0a]
                  "
                >
                  Explore the investigations
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#0d0b09] px-5 py-14 sm:px-8 sm:py-16">
        <div className="mx-auto max-w-6xl">
          <div className="text-center">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.24em] text-amber-400">
              Two ways into the past
            </p>
            <h2 className="text-2xl font-bold text-amber-100 sm:text-3xl">
              Begin with the story—or begin with the evidence
            </h2>
          </div>

          <div className="mt-9 grid gap-5 md:grid-cols-2">
            <article className="rounded-xl border border-white/10 bg-[#191512] p-7">
              <BookOpen
                className="mb-5 h-7 w-7 text-amber-300"
                aria-hidden="true"
              />
              <h3 className="text-xl font-semibold text-amber-200">
                Enter through story
              </h3>
              <p className="mt-3 leading-7 text-stone-300">
                Meet the characters of the Sons of the Standard trilogy and
                experience Roman Britain through conflict, loyalty, belief, and
                survival.
              </p>
              <Link
                to="/books"
                className="mt-5 inline-flex items-center gap-2 font-semibold text-amber-300 hover:text-amber-200"
              >
                Discover the books
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </article>

            <article className="rounded-xl border border-white/10 bg-[#191512] p-7">
              <Library
                className="mb-5 h-7 w-7 text-amber-300"
                aria-hidden="true"
              />
              <h3 className="text-xl font-semibold text-amber-200">
                Enter through evidence
              </h3>
              <p className="mt-3 leading-7 text-stone-300">
                Browse research papers, maps, timelines, military dossiers, and
                archaeological sources that reveal the older world beneath the
                present landscape.
              </p>
              <Link
                to="/library"
                className="mt-5 inline-flex items-center gap-2 font-semibold text-amber-300 hover:text-amber-200"
              >
                Enter the research library
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </article>
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 bg-[#090806] px-5 py-14 sm:px-8 sm:py-16">
        <div className="mx-auto max-w-6xl">
          <div className="max-w-3xl">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.24em] text-amber-400">
              Research routes
            </p>
            <h2 className="text-2xl font-bold text-amber-100 sm:text-3xl">
              Choose how deeply you want to explore
            </h2>
            <p className="mt-4 leading-7 text-stone-300">
              Search the complete paper archive, follow a military theme, or
              use a guided pathway through the conquest of Britannia.
            </p>
          </div>

          <div className="mt-8 grid gap-5 lg:grid-cols-3">
            {researchRoutes.map((route) => {
              const Icon = route.icon;

              return (
                <Link
                  key={route.path}
                  to={route.path}
                  className="
                    group rounded-xl border border-white/10 bg-[#15120f] p-6
                    transition duration-200
                    hover:border-amber-500/45 hover:bg-[#1d1814]
                    focus:outline-none focus:ring-2 focus:ring-amber-300
                    focus:ring-offset-2 focus:ring-offset-[#090806]
                  "
                >
                  <Icon
                    className="mb-4 h-6 w-6 text-amber-300"
                    aria-hidden="true"
                  />
                  <h3 className="text-lg font-semibold text-amber-200">
                    {route.title}
                  </h3>
                  <p className="mt-3 leading-7 text-stone-300">
                    {route.description}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-amber-300">
                    Open
                    <ArrowRight
                      className="h-4 w-4 transition-transform group-hover:translate-x-1"
                      aria-hidden="true"
                    />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 bg-black px-5 py-10 sm:px-8">
        <div className="mx-auto flex max-w-6xl flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="font-semibold text-amber-200">Roman Storyworld</p>
            <p className="mt-2 max-w-xl text-sm leading-6 text-stone-400">
              Historical fiction, landscape research, and the archaeology of
              Roman Britain.
            </p>
            <p className="mt-3 text-xs text-stone-500">
              © Martin Antrobus
            </p>
          </div>

          <nav
            className="flex flex-wrap gap-x-5 gap-y-3 text-sm text-stone-300"
            aria-label="Footer navigation"
          >
            <Link className="hover:text-amber-200" to="/books">
              Books
            </Link>
            <Link className="hover:text-amber-200" to="/library">
              Research
            </Link>
            <Link className="hover:text-amber-200" to="/maps">
              Maps
            </Link>
            <Link className="hover:text-amber-200" to="/timeline">
              Timeline
            </Link>
            <Link className="hover:text-amber-200" to="/about">
              About
            </Link>
          </nav>
        </div>
      </footer>
    </main>
  );
};

export default Home;
